import * as React from "react";
import {
  Platform,
  Text,
  TextInputProps as TextInputPropsRN,
  TextInput as TextInputRN,
  View,
} from "react-native";
import { BottomSheetTextInput } from "@gorhom/bottom-sheet";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { colors } from "../colors";

interface TextInputProps
  extends TextInputPropsRN,
    VariantProps<typeof textInputStyles> {
  label?: string;
  hint?: string;
  required?: boolean;
  inBottomSheet?: boolean;
  errorMessage?: string;
  dataSet?: Record<string, string>;
  wrapperClassName?: string;
}

export const TextInput = React.forwardRef<
  React.ElementRef<typeof TextInputRN>,
  TextInputProps
>(function TextInput(props, ref) {
  const inputRef = React.useRef<TextInputRN>(null);
  React.useImperativeHandle(ref, () => inputRef.current!);

  const {
    id,
    label,
    errorMessage,
    required,
    placeholder,
    inBottomSheet,
    hint,
    size,
    wrapperClassName,
    ...rest
  } = props;

  const reactId = React.useId();
  const fieldId = id || reactId;
  const labelId = `${fieldId}-label`;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  const Comp =
    Platform.OS !== "web" && inBottomSheet
      ? (BottomSheetTextInput as unknown as typeof TextInputRN)
      : TextInputRN;

  return (
    <View className={cn("gap-2", wrapperClassName)}>
      {(label || required) && (
        <View className="flex-row items-center gap-x-2" id={labelId}>
          {label && (
            <Text
              className="text-input-label text-base font-semibold"
              onPress={() => inputRef.current?.focus()}
            >
              {label}
            </Text>
          )}
          {required && (
            <Text className="text-text-tertiary text-base font-semibold">
              (required)
            </Text>
          )}
        </View>
      )}
      {hint && (
        <Text
          id={hintId}
          onPress={() => inputRef.current?.focus()}
          className="text-text-tertiary text-base font-semibold"
        >
          {hint}
        </Text>
      )}
      <Comp
        ref={inputRef}
        className={cn(textInputStyles({ size }))}
        placeholder={placeholder}
        placeholderTextColor={colors.input.text.default}
        id={fieldId}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        aria-invalid={!!errorMessage}
        aria-errormessage={errorId}
        {...rest}
      />

      {errorMessage && (
        <Text className="text-red-500" id={errorId}>
          {errorMessage}
        </Text>
      )}
    </View>
  );
});

const textInputStyles = cva(
  "border-input-border focus:bg-input-background-active focus:border-input-border-active text-text-primary rounded-lg border-2 outline-none",
  {
    variants: {
      size: {
        medium: "px-4 py-3 h-12",
        small: "px-4 py-2 h-9",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  },
);
